import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { normalizePhone, cleanPhoneInput } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CurrencyInr, Pencil, Trash, Plus, ArrowClockwise, MagnifyingGlass, FilePdf, CaretDown, CaretRight } from "@phosphor-icons/react";
import { istToday, istDate } from "@/lib/istDate";

const fmtInr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Present date-range presets — matches the way owners think ("aaj ka",
// "is hafte ka", "is mahine ka") instead of forcing them to pick dates.
const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

// Format Date as local YYYY-MM-DD (avoid UTC shift for IST users)
function _localIso(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return istDate(d);
}

// Historical payments migrated with only a date component are stored as
// "T00:00:00Z" — showing the time in IST renders "5:30:00 pm" which confuses
// the owner. If the original UTC time is midnight, we treat it as date-only.
function isMidnightUtc(iso) {
  if (!iso) return true;
  try {
    const d = new Date(iso);
    return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  } catch { return false; }
}
function _todayIso() {
  return _localIso(new Date());
}
function _rangeDates(key) {
  const now = new Date();
  const to = _todayIso();
  if (key === "today") return { from: to, to };
  if (key === "week") {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: _localIso(d), to };
  }
  // month
  const d = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: _localIso(d), to };
}

export default function PaymentsPage() {
  const { user } = useAuth();
  const isSecondary = user?.role === "secondary_admin" || user?.role === "accountant";
  const isSecAdmin = user?.role === "secondary_admin";
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState(_rangeDates("today").from);
  const [to, setTo] = useState(_rangeDates("today").to);
  const [data, setData] = useState({ total: 0, payments: [], by_mode: [] });
  const [loading, setLoading] = useState(false);
  const [modes, setModes] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({}); // group_id -> bool
  const [modeFilter, setModeFilter] = useState(null); // key or null
  const [proofPreview, setProofPreview] = useState(null); // { url, filename }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/payments", { params: { from, to } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const loadModes = async () => {
    try {
      const { data } = await api.get("/settings/payment-modes");
      setModes(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadModes(); /* eslint-disable-next-line */ }, [from, to]);

  const setPreset = (k) => {
    // Secondary Admin is locked to today's data (per RBAC — Total Sale hide)
    if (isSecAdmin ? !(k === "today" || k === "week") : isSecondary) return;
    setRange(k);
    if (k !== "custom") {
      const { from: f, to: t } = _rangeDates(k);
      setFrom(f); setTo(t);
    }
  };

  const modeLabel = (key) => modes.find(m => m.key === key)?.label || key;

  // Prefer party-wise grouped rows if the backend supplies them. Falls back to
  // raw splits for old backends without `grouped`. Each group carries an
  // `entries[]` list of the underlying per-order splits so edit/delete still
  // works on the underlying payment_id.
  const rawGroups = data.grouped && data.grouped.length ? data.grouped : (data.payments || []).map(p => ({
    group_id: p.payment_id || p.advance_id,
    date: p.date,
    mobile: p.mobile,
    customer_name: p.customer_name,
    mode: p.mode,
    mode_label: p.mode_label,
    note: p.note,
    recorded_by_name: p.recorded_by_name,
    kind: p.kind,
    total_amount: p.amount,
    order_count: p.order_id ? 1 : 0,
    entries: [{
      payment_id: p.payment_id, order_id: p.order_id,
      order_number: p.order_number, amount: p.amount,
      advance_id: p.advance_id, kind: p.kind,
    }],
  }));

  // Client-side search across group's party name, mobile, mode, note, and any
  // order number it touches (owner types "1234" and sees the party payment
  // that included that order).
  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    let rows = rawGroups;
    if (modeFilter) rows = rows.filter((g) => g.mode === modeFilter);
    if (!q) return rows;
    return rows.filter((g) => {
      if ((g.customer_name || "").toLowerCase().includes(q)) return true;
      if ((g.mobile || "").toLowerCase().includes(q)) return true;
      if ((g.note || "").toLowerCase().includes(q)) return true;
      if ((g.mode_label || g.mode || "").toLowerCase().includes(q)) return true;
      return (g.entries || []).some((e) => (e.order_number || "").toLowerCase().includes(q));
    });
  }, [rawGroups, q, modeFilter]);

  const totalCount = filteredGroups.length;
  const filteredTotal = useMemo(() => (
    q ? filteredGroups.reduce((s, g) => s + Number(g.total_amount || 0), 0) : Number(data.total || 0)
  ), [q, filteredGroups, data.total]);

  const downloadPdf = async () => {
    // Uses the backend's report_payments_pdf endpoint. Kept simple — we build
    // a signed URL via fetch to include the JWT so the browser saves directly.
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const resp = await api.get(`/reports/payments/pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PaymentIn_${from}_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF download failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-heading tracking-widest text-white">PAYMENT IN</h2>
          <p className="text-xs font-mono-num tracking-wider text-slate-500 mt-1">
            // Received payments across all orders + standalone advances
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setAdvanceOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 rounded-sm h-10 px-4 text-white font-heading tracking-widest"
            data-testid="take-advance-btn"
          >
            <Plus size={18} className="mr-1" /> Take Advance
          </Button>
          <Button onClick={downloadPdf} variant="outline"
            className="rounded-sm h-10 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="export-pdf-btn">
            <FilePdf size={16} className="mr-1" /> Export PDF
          </Button>
          <Button onClick={load} variant="outline"
            className="rounded-sm h-10 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="refresh-btn">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Range presets + Search */}
      <div className="flex items-center gap-2 flex-wrap">
        {isSecAdmin ? RANGES.filter(r => r.key === "today" || r.key === "week").map(r => (
          <button key={r.key} onClick={() => setPreset(r.key)}
            className={
              "px-3 h-9 rounded-sm border font-mono-num text-xs tracking-widest transition-colors " +
              (range === r.key
                ? "bg-orange-500/20 border-orange-500 text-orange-300"
                : "bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-500")
            }
            data-testid={`range-${r.key}`}
          >
            {r.label.toUpperCase()}
          </button>
        )) : isSecondary ? (
          <span className="px-3 h-9 rounded-sm border bg-orange-500/20 border-orange-500 text-orange-300 font-mono-num text-xs tracking-widest inline-flex items-center"
            data-testid="range-locked-today">TODAY (locked)</span>
        ) : RANGES.map(r => (
          <button key={r.key} onClick={() => setPreset(r.key)}
            className={
              "px-3 h-9 rounded-sm border font-mono-num text-xs tracking-widest transition-colors " +
              (range === r.key
                ? "bg-orange-500/20 border-orange-500 text-orange-300"
                : "bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-500")
            }
            data-testid={`range-${r.key}`}
          >
            {r.label.toUpperCase()}
          </button>
        ))}
        {!isSecondary && range === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num w-40"
              data-testid="from-date"/>
            <span className="text-slate-500 text-xs">→</span>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num w-40"
              data-testid="to-date"/>
          </div>
        )}
        <div className="relative flex-1 min-w-[220px] ml-2 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, mobile, order no. or note…"
            className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num pl-9 pr-8"
            data-testid="payments-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 text-xs"
              data-testid="payments-search-clear"
              title="Clear search"
            >✕</button>
          )}
        </div>
      </div>

      {/* Summary cards — Total + one card PER payment mode (Cash, IndusInd, Axis, UPI, …).
          Grid grows to fit all modes so the owner can reconcile every bucket at a glance. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        <div className="surface p-4 rounded-sm border-l-4 border-emerald-500" data-testid="total-received-card">
          <div className="text-xs font-mono-num text-slate-400 tracking-widest">// TOTAL RECEIVED {q && <span className="text-orange-400">(filtered)</span>}</div>
          <div className="text-3xl font-heading text-emerald-300 mt-1">{fmtInr(filteredTotal)}</div>
          <div className="text-xs font-mono-num text-slate-500 mt-1">{totalCount} entries</div>
        </div>
        {(data.by_mode || []).map(m => (
          <button
            key={m.key}
            onClick={() => setModeFilter(modeFilter === m.key ? null : m.key)}
            className={
              "surface p-4 rounded-sm text-left transition-all border-l-4 " +
              (modeFilter === m.key
                ? "border-orange-500 bg-orange-500/10 ring-2 ring-orange-500/40"
                : "border-orange-500 hover:bg-slate-800/40")
            }
            data-testid={`mode-card-${m.key}`}
          >
            <div className="text-xs font-mono-num text-slate-400 tracking-widest">
              // {m.label?.toUpperCase()} {modeFilter === m.key && <span className="text-orange-400">● FILTERED</span>}
            </div>
            <div className="text-2xl font-heading text-orange-300 mt-1">{fmtInr(m.amount)}</div>
            <div className="text-xs font-mono-num text-slate-500 mt-1">{m.count} entries · click to filter</div>
          </button>
        ))}
      </div>

      {/* Transactions table */}
      <div className="surface rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-slate-700 bg-slate-800/40">
            <tr className="text-left text-slate-400 font-mono-num text-xs tracking-widest">
              <th className="px-4 py-3 w-6"></th>
              <th className="px-4 py-3">DATE</th>
              <th className="px-4 py-3">PARTY</th>
              <th className="px-4 py-3">ORDERS</th>
              <th className="px-4 py-3">MODE</th>
              <th className="px-4 py-3">BY</th>
              <th className="px-4 py-3 text-right">AMOUNT</th>
              <th className="px-4 py-3 text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && totalCount === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500 font-mono-num text-xs">
                {q ? `No payments matching "${query}" in this range.` : "No payments in this range."}
              </td></tr>
            )}
            {filteredGroups.map((g) => {
              const isAdvance = g.kind === "advance";
              const canExpand = (g.entries || []).length > 1;
              const isOpen = !!expanded[g.group_id];
              // Single-entry group can inline edit/delete directly.
              const singleEntry = g.entries?.[0];
              return (
              <React.Fragment key={g.group_id}>
                <tr className={"border-b border-slate-800 hover:bg-slate-800/30 " + (isAdvance ? "bg-emerald-500/[0.03]" : "")}
                    data-testid={`payment-group-${g.group_id}`}>
                  <td className="px-2 py-2.5 text-center align-top">
                    {canExpand && (
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [g.group_id]: !s[g.group_id] }))}
                        className="text-slate-500 hover:text-orange-300 transition-colors"
                        data-testid={`expand-group-${g.group_id}`}
                        title={isOpen ? "Hide split" : "Show split across orders"}
                      >{isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}</button>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono-num text-xs text-slate-400 align-top">
                    {new Date(g.date).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <div className="text-white">{g.customer_name || "—"}</div>
                    <div className="text-[10px] font-mono-num text-slate-500">{g.mobile || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 font-mono-num text-xs align-top">
                    {isAdvance ? (
                      <span className="inline-block px-2 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/50 text-emerald-300 tracking-widest">↳ ADVANCE</span>
                    ) : canExpand ? (
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-block px-2 py-0.5 rounded-sm bg-orange-500/15 border border-orange-500/40 text-orange-300 tracking-widest text-[10px]">
                          {g.entries.length} orders
                        </span>
                        {g.entries.slice(0, 2).map((e) => (
                          <span key={e.payment_id} className="text-orange-300/70 text-[10px]">#{e.order_number}</span>
                        ))}
                        {g.entries.length > 2 && <span className="text-slate-500 text-[10px]">+{g.entries.length - 2} more</span>}
                      </div>
                    ) : (
                      <span className="text-orange-300">#{singleEntry?.order_number || "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <span className="inline-block px-2 py-0.5 rounded-sm bg-slate-700/50 border border-slate-600 text-[10px] font-mono-num tracking-widest text-slate-300">
                      {g.mode_label?.toUpperCase() || g.mode}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs align-top">
                    <div className="text-slate-300">{g.recorded_by_name || "—"}</div>
                    {g.note && <div className="text-[10px] text-slate-500 mt-0.5">{g.note}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-emerald-300 font-bold align-top">
                    {fmtInr(g.total_amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      {g.proof_upload_id && (
                        <button
                          onClick={async () => {
                            try {
                              const resp = await api.get(`/delivery/proof/${g.proof_upload_id}`, { responseType: "blob" });
                              const url = URL.createObjectURL(resp.data);
                              setProofPreview({ url, filename: "Payment proof" });
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Photo load failed"); }
                          }}
                          className="h-8 w-8 rounded-sm border border-slate-700 hover:border-cyan-500 hover:bg-cyan-500/10 text-slate-400 hover:text-cyan-300 inline-flex items-center justify-center transition-colors"
                          title="View payment proof photo"
                          data-testid={`view-proof-${g.group_id}`}
                        >📷</button>
                      )}
                      {isAdvance ? (
                        <button
                          disabled={!!singleEntry?.consumed}
                          onClick={async () => {
                            if (!window.confirm(`Delete standalone advance ${fmtInr(g.total_amount)} for ${g.customer_name}?\nParty credit will be reduced.`)) return;
                            try {
                              await api.delete(`/parties/advance/${singleEntry.advance_id}`);
                              toast.success("Advance deleted");
                              load();
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                          }}
                          className="h-8 w-8 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Delete advance"
                          data-testid={`delete-advance-${singleEntry?.advance_id}`}
                        ><Trash size={14} /></button>
                      ) : canExpand ? (
                        <>
                          <button
                            onClick={() => setEditRow({
                              // Bulk-edit marker — dialog will PATCH every split under this group.
                              group_id: g.group_id,
                              entries: g.entries,
                              order_id: g.entries[0].order_id,
                              payment_id: g.entries[0].payment_id,
                              order_number: `${g.entries.length} orders`,
                              customer_name: g.customer_name,
                              amount: g.total_amount,
                              date: g.date,
                              mode: g.mode, mode_label: g.mode_label, note: g.note,
                              mode_new: g.mode, note_new: g.note || "", amount_new: g.total_amount,
                              _isGroup: true,
                            })}
                            className="h-8 w-8 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 text-slate-400 hover:text-orange-300 inline-flex items-center justify-center transition-colors"
                            title="Edit all splits in this party payment"
                            data-testid={`edit-group-${g.group_id}`}
                          ><Pencil size={14} /></button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete this party payment (${g.entries.length} splits, total ${fmtInr(g.total_amount)})?\nSabhi allocations remove ho jaayenge aur orders ke balance recompute honge.`)) return;
                              try {
                                for (const en of g.entries) {
                                  await api.delete(`/orders/${en.order_id}/payments/${en.payment_id}`);
                                }
                                toast.success("Party payment deleted");
                                load();
                              } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                            }}
                            className="h-8 w-8 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center transition-colors"
                            title="Delete all splits in this party payment"
                            data-testid={`delete-group-${g.group_id}`}
                          ><Trash size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditRow({
                              order_id: singleEntry.order_id,
                              payment_id: singleEntry.payment_id,
                              order_number: singleEntry.order_number,
                              customer_name: g.customer_name,
                              amount: singleEntry.amount,
                              date: g.date,
                              mode: g.mode, mode_label: g.mode_label, note: g.note,
                              mode_new: g.mode, note_new: g.note || "", amount_new: singleEntry.amount,
                            })}
                            className="h-8 w-8 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 text-slate-400 hover:text-orange-300 inline-flex items-center justify-center transition-colors"
                            title="Edit"
                            data-testid={`edit-payment-${singleEntry?.payment_id}`}
                          ><Pencil size={14} /></button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete payment ${fmtInr(singleEntry.amount)} for #${singleEntry.order_number}?\nOrder balance will be recomputed.`)) return;
                              try {
                                await api.delete(`/orders/${singleEntry.order_id}/payments/${singleEntry.payment_id}`);
                                toast.success("Payment deleted");
                                load();
                              } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                            }}
                            className="h-8 w-8 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center transition-colors"
                            title="Delete"
                            data-testid={`delete-payment-${singleEntry?.payment_id}`}
                          ><Trash size={14} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* When a party payment split across multiple orders, show each
                    allocation as an indented sub-row so the owner can still
                    edit / delete individual splits if needed. */}
                {canExpand && isOpen && (g.entries || []).map((e) => (
                  <tr key={e.payment_id} className="bg-slate-900/40 border-b border-slate-800/60" data-testid={`split-row-${e.payment_id}`}>
                    <td></td>
                    <td className="px-4 py-2 text-[10px] font-mono-num text-slate-500">↳</td>
                    <td className="px-4 py-2 text-xs text-slate-500">split allocation</td>
                    <td className="px-4 py-2 font-mono-num text-xs text-orange-300/80">#{e.order_number}</td>
                    <td></td>
                    <td></td>
                    <td className="px-4 py-2 text-right font-mono-num text-emerald-300/80 text-sm">{fmtInr(e.amount)}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditRow({
                            order_id: e.order_id, payment_id: e.payment_id,
                            order_number: e.order_number, customer_name: g.customer_name,
                            amount: e.amount, date: g.date,
                            mode: g.mode, mode_label: g.mode_label, note: g.note,
                            mode_new: g.mode, note_new: g.note || "", amount_new: e.amount,
                          })}
                          className="h-7 w-7 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 text-slate-400 hover:text-orange-300 inline-flex items-center justify-center"
                          title="Edit this split"
                          data-testid={`edit-split-${e.payment_id}`}
                        ><Pencil size={12} /></button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete ${fmtInr(e.amount)} allocation to #${e.order_number}?`)) return;
                            try {
                              await api.delete(`/orders/${e.order_id}/payments/${e.payment_id}`);
                              toast.success("Split deleted");
                              load();
                            } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
                          }}
                          className="h-7 w-7 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center"
                          title="Delete this split"
                          data-testid={`delete-split-${e.payment_id}`}
                        ><Trash size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <PaymentEditDialog
        row={editRow} modes={modes}
        onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); load(); }}
      />

      {/* Take Standalone Advance Dialog */}
      <TakeAdvanceDialog
        open={advanceOpen}
        modes={modes}
        onClose={() => setAdvanceOpen(false)}
        onSaved={() => { setAdvanceOpen(false); load(); }}
      />

      {/* Payment proof photo preview */}
      <Dialog open={!!proofPreview} onOpenChange={(v) => {
        if (!v && proofPreview?.url) URL.revokeObjectURL(proofPreview.url);
        if (!v) setProofPreview(null);
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl bg-slate-900 border-slate-700 p-0 gap-0" data-testid="proof-preview-dialog">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/60">
            <DialogTitle className="text-white font-heading tracking-widest">PAYMENT PROOF</DialogTitle>
            <button onClick={() => {
              if (proofPreview?.url) URL.revokeObjectURL(proofPreview.url);
              setProofPreview(null);
            }} className="h-10 w-10 rounded-sm border border-slate-600 hover:border-red-500 hover:bg-red-500/10 text-slate-300 hover:text-red-300 inline-flex items-center justify-center text-2xl font-bold" data-testid="proof-close-btn">✕</button>
          </div>
          {proofPreview && (
            <div className="p-3 bg-slate-950">
              <img src={proofPreview.url} alt="proof" className="w-full h-auto max-h-[75vh] mx-auto object-contain rounded-sm border border-slate-700" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PaymentEditDialog({ row, modes, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("");
  const [note, setNote] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) {
      setAmount(String(row.amount || ""));
      setMode(row.mode || "");
      setNote(row.note || "");
      try {
        const d = new Date(row.date);
        const pad = (n) => String(n).padStart(2, "0");
        setDateStr(istDate(d));
      } catch { setDateStr(""); }
    }
  }, [row]);

  if (!row) return null;

  const submit = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Amount valid daaliye"); return; }
    if (!mode) { toast.error("Mode select karo"); return; }
    let origDate = "";
    try {
      const d = new Date(row.date);
      const pad = (v) => String(v).padStart(2, "0");
      origDate = istDate(d);
    } catch {}
    setBusy(true);
    try {
      // Bulk-edit path: apply mode / date / note to every split under this
      // party-payment group. Amount is NOT changed for a group edit (each
      // split has its own amount from the FIFO distribution).
      if (row._isGroup && Array.isArray(row.entries)) {
        for (const en of row.entries) {
          const patch = { mode, note };
          if (dateStr && dateStr !== origDate) patch.created_at = dateStr;
          await api.patch(`/orders/${en.order_id}/payments/${en.payment_id}`, patch);
        }
        toast.success(`${row.entries.length} splits updated`);
        onSaved();
        return;
      }
      const payload = { amount: n, mode, note };
      if (dateStr && dateStr !== origDate) payload.created_at = dateStr;
      await api.patch(`/orders/${row.order_id}/payments/${row.payment_id}`, payload);
      toast.success(`Payment updated · ${fmtInr(n)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 rounded-sm max-w-md" data-testid="edit-payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading tracking-widest">EDIT PAYMENT</DialogTitle>
          <p className="text-xs font-mono-num text-slate-500 mt-1">
            #{row.order_number} · {row.customer_name}
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
            <Input type="number" step="0.01" min="0" value={amount}
              onChange={e => setAmount(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg mt-1"
              data-testid="edit-amount-input"
              autoFocus/>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1" data-testid="edit-mode-select">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                {modes.filter(m => m.enabled !== false).map(m => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DATE</Label>
            <Input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
              data-testid="edit-date-input"/>
            <div className="text-[10px] text-slate-500 mt-1">
              Galti se aaj ka date ho gaya? Party ne jab actually diya tha, wo date daaliye.
            </div>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1"
              data-testid="edit-note-input"/>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button type="submit" disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest rounded-sm h-11 px-5"
              data-testid="edit-submit-btn">
              <CurrencyInr size={16} weight="bold" className="mr-1" />
              {busy ? "SAVING…" : "SAVE"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function TakeAdvanceDialog({ open, modes, onClose, onSaved }) {
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [note, setNote] = useState("");
  const [paidOn, setPaidOn] = useState(istToday());
  const [busy, setBusy] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  useEffect(() => {
    if (open) {
      setMobile(""); setName(""); setAddress(""); setAmount(""); setMode("cash"); setNote("");
      setPaidOn(istToday());
      setAutoFilled(false);
    }
  }, [open]);

  // Auto-fetch existing party's name / address when user finishes typing mobile
  useEffect(() => {
    if (!open) return;
    if (mobile.length !== 10) { setAutoFilled(false); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/parties/${mobile}/credit`);
        if (cancelled) return;
        if (data?.customer_name) {
          setName(data.customer_name);
          setAutoFilled(true);
        } else {
          // Try /parties list for name+address of an existing party without credit
          const { data: pl } = await api.get("/parties");
          const found = (pl?.parties || []).find((p) => (p.mobile || "").endsWith(mobile));
          if (found && !cancelled) {
            setName(found.customer_name || "");
            setAddress(found.address || "");
            setAutoFilled(true);
          }
        }
      } catch (_e) { /* silent */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mobile, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!normalizePhone(mobile)) { toast.error("Invalid mobile number"); return; }
    if (!name.trim()) { toast.error("Customer name daaliye"); return; }
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Amount valid daaliye"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/parties/advance", {
        mobile, customer_name: name, address, amount: n, mode, note, paid_on: paidOn,
      });
      toast.success(`Advance ₹${n.toLocaleString("en-IN")} saved · credit ${fmtInr(data.credit_balance)}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 rounded-sm max-w-md" data-testid="take-advance-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading tracking-widest">TAKE ADVANCE</DialogTitle>
          <p className="text-xs font-mono-num text-slate-500 mt-1">
            Customer se paise le rahe ho order lene se pahle. Ye credit ke roop me party ke naam par save hoga.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MOBILE *</Label>
            <Input value={mobile} onChange={e => { const v = e.target.value; setMobile(normalizePhone(v) || v.replace(/\D/g, "").slice(0, 12)); }}
              onBlur={() => setMobile(cleanPhoneInput(mobile))}
              placeholder="9876543210" inputMode="numeric"
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num mt-1"
              data-testid="advance-mobile"
              autoFocus/>
            {autoFilled && (
              <div className="text-[10px] font-mono-num text-cyan-400 mt-1" data-testid="advance-existing-party-badge">
                ✓ EXISTING PARTY — details auto-filled
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">CUSTOMER NAME *</Label>
            <Input value={name} onChange={e => setName(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1"
              data-testid="advance-name"/>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">ADDRESS (OPTIONAL)</Label>
            <Input value={address} onChange={e => setAddress(e.target.value)}
              placeholder="Shop / area / landmark"
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1"
              data-testid="advance-address"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT *</Label>
              <Input type="number" step="0.01" min="0" value={amount}
                onChange={e => setAmount(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg mt-1"
                data-testid="advance-amount"/>
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1" data-testid="advance-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {modes.filter(m => m.enabled !== false).map(m => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">PAID ON</Label>
            <Input type="date" value={paidOn}
              max={istToday()}
              onChange={e => setPaidOn(e.target.value)}
              className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num mt-1"
              data-testid="advance-paidon"/>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)}
              placeholder="e.g., cheque #12345"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1"
              data-testid="advance-note"/>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button type="submit" disabled={busy}
              className="bg-orange-500 hover:bg-orange-600 text-white font-heading tracking-widest rounded-sm h-11 px-5"
              data-testid="advance-submit-btn">
              <CurrencyInr size={16} weight="bold" className="mr-1" />
              {busy ? "SAVING…" : "TAKE ADVANCE"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
